<template>
    <div class="general-modal">
        <BaseModal :padding="isMobileOrTablet ? MODAL_PADDING_MOBILE : MODAL_PADDING_DESKTOP"
                   maxWidth="550px"
                   maxHeight="500px"
                   useContentFullHeight
                   useClickOutsideClose
                   @close="handleClose">
            <template #header />
            <template #content>
                <div class="general-modal__content general-modal__content--justify-center">
                    <template v-if="screenIndex === 0">
                        <div class="general-modal__title">
                            <Translate v-if="!authStore.user?.email"
                                       translation-key="quests-dashboard.addEmailAddress"
                                       fallback="Add email address" />
                            <Translate v-else-if="!!authStore.user?.is_email_verified"
                                       translation-key="quests-dashboard.changeAndVerifyYourEmail"
                                       fallback="Change & verify your email" />
                            <template v-else>
                                <Translate translationKey="quests-dashboard.verifyYourEmail"
                                           fallback="Verify your email" />!
                            </template>
                        </div>
                        <EmailInputForm v-model="emailValue"
                                        :initialEmail="authStore.user?.email"
                                        :isEmailVerified="!!authStore.user?.is_email_verified"
                                        :backendError="backendError"
                                        @emailSubmitAvailable="handleEmailSubmitAvailable" />
                    </template>
                    <template v-else-if="screenIndex === 1">
                        <BaseCloseButton icon="fa-solid fa-chevron-left"
                                         class="previous-button"
                                         @click="getBackToEmailScreen" />
                        <div class="general-modal__title">
                            <Translate translationKey="quests-dashboard.typeVerification"
                                       fallback="Type in verification code" />
                        </div>
                        <div class="general-modal__subtitle">
                            <Translate translationKey="quests-dashboard.typeVerificationSubtitle"
                                       fallback="Verification code has been sent to your email address. Enter it below." />
                        </div>
                        <VerificationCodeInputForm :backendError="backendError"
                                                   :timer="timer"
                                                   @submitCode="handleVerificationCode"
                                                   @resendCode="handleResendVerificationCode"
                                                   @verificationSubmitAvailable="handleVerificationSubmitAvailable" />
                    </template>
                </div>
            </template>
            <template #footer>
                <div class="general-modal__actions">
                    <span v-if="screenIndex === 0"
                          class="general-modal__action general-modal__action--confirm">
                        <BaseButton v-if="timer > 0 && !showButtonLoader"
                                    :variant="ButtonVariantEnum.SECONDARY"
                                    disabled>
                            <Translate translationKey="quests-dashboard.verifyIn"
                                       :replacements="{seconds: timer}" />
                        </BaseButton>
                        <BaseButton v-else
                                    :variant="ButtonVariantEnum.SECONDARY"
                                    :disabled="!emailSubmitAvailable"
                                    :loading="showButtonLoader"
                                    :loaderVariant="!emailSubmitAvailable ? LoaderVariantsEnum.WHITE : LoaderVariantsEnum.BLACK"
                                    @click="handleEmailSubmit">
                            <Translate v-if="!authStore.user?.email"
                                       translation-key="quests-dashboard.submit"
                                       fallback="Submit" />
                            <Translate v-else
                                       translation-key="quests-dashboard.verify"
                                       fallback="Verify" />
                        </BaseButton>
                    </span>
                    <span v-if="screenIndex === 1"
                          class="general-modal__action general-modal__action--confirm">
                        <BaseButton :variant="ButtonVariantEnum.SECONDARY"
                                    :disabled="!verificationSubmitAvailable"
                                    :loading="showButtonLoader"
                                    :loaderVariant="!verificationSubmitAvailable ? LoaderVariantsEnum.WHITE : LoaderVariantsEnum.BLACK"
                                    @click="handleVerificationCodeSubmit">
                            <Translate translationKey="quests-dashboard.submit"
                                       fallback="Submit" />
                        </BaseButton>
                    </span>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<script setup lang="ts">
    import { onBeforeUnmount, ref, watch } from 'vue';
    import BaseModal from '@/js/components/BaseModal.vue';
    import { ModalEnum, useModalStore } from '@/js/store/quests/game/modules/ModalStore';
    import { useModalActive } from '@/js/composables/useModalActive';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { MODAL_PADDING_DESKTOP, MODAL_PADDING_MOBILE } from '@/js/quests/global/constants/modal';
    import { useAuthStore } from '@/js/store/auth/AuthStore';
    import EmailInputForm from '@/js/quests/dashboard/modals/email-verify/EmailInputForm.vue';
    import VerificationCodeInputForm from '@/js/quests/dashboard/modals/email-verify/VerificationCodeInputForm.vue';
    import { useUserStore } from '@/js/store/quests/dashboard/UserStore';
    import BaseCloseButton from '@/js/components/BaseCloseButton.vue';
    import { LoaderVariantsEnum } from '@/js/enums/loaderVariants';
    import { ButtonVariantEnum } from '@/js/types/components/ButtonVariantEnum';

    const authStore = useAuthStore();
    const userStore = useUserStore();
    const modalStore = useModalStore();
    const { isMobileOrTablet } = useDeviceSize();

    const emailValue = ref('');
    const screenIndex = ref(0);
    const backendError = ref<string | null>(null);
    const showButtonLoader = ref(false);
    const timer = ref(0);
    const intervalId = ref<ReturnType<typeof setTimeout> | null>(null);

    const emailSubmitAvailable = ref(false);
    const verificationSubmitAvailable = ref(false);
    const verificationCode = ref('');

    const resetData = () => {
        emailValue.value = authStore.user?.email ? authStore.user.email : '';
        screenIndex.value = 0;
        backendError.value = null;
        showButtonLoader.value = false;
        timer.value = 0;
        if (intervalId.value) {
            clearTimeout(intervalId.value);
            intervalId.value = null;
        }
    };

    const handleClose = () => {
        resetData();
        modalStore.toggleModal(ModalEnum.EMAIL_VERIFY, false);
    };

    const startTimer = () => {
        if (intervalId.value) {
            clearTimeout(intervalId.value);
        }
        timer.value = 60;
        const tick = () => {
            if (timer.value > 0) {
                timer.value -= 1;
                intervalId.value = setTimeout(tick, 1000);
            } else {
                intervalId.value = null;
            }
        };
        intervalId.value = setTimeout(tick, 1000);
    };

    const handleEmailSubmit = async () => {
        showButtonLoader.value = true;
        backendError.value = null;
        try {
            await userStore.sendEmailVerification(emailValue.value);
            screenIndex.value = 1;
            startTimer();
        } catch (error) {
            if (error.response?.data && error.response?.data.message) {
                backendError.value = error.response.data.message;
            } else {
                backendError.value = 'An unexpected error occurred.';
            }
        } finally {
            showButtonLoader.value = false;
        }
    };

    const handleVerificationCodeSubmit = async () => {
        if (!verificationSubmitAvailable.value) {
            return;
        }

        showButtonLoader.value = true;
        backendError.value = null;
        try {
            await userStore.verifyEmailVerificationCode(verificationCode.value);
            authStore.setEmail(emailValue.value);
            authStore.user.is_email_verified = true;
            authStore.user.must_verify_email = false;
            handleClose();

            if (!authStore.user?.is_email_connect_enabled) {
                modalStore.toggleModal(ModalEnum.ADD_PASSWORD, true);
            }
        } catch (error) {
            if (error.response?.data && error.response?.data.message) {
                backendError.value = error.response.data.message;
            } else {
                backendError.value = 'Invalid verification code.';
            }
        } finally {
            showButtonLoader.value = false;
        }
    };

    const handleResendVerificationCode = async () => {
        backendError.value = null;
        try {
            await userStore.sendEmailVerification(emailValue.value);
            startTimer();
        } catch (error) {
            if (error.response?.data && error.response?.data.message) {
                backendError.value = error.response.data.message;
            } else {
                backendError.value = 'Failed to resend code.';
            }
        }
    };

    const getBackToEmailScreen = () => {
        backendError.value = null;
        if (intervalId.value) {
            clearTimeout(intervalId.value);
            intervalId.value = null;
        }
        timer.value = 0;
        screenIndex.value = 0;
    };

    const handleEmailSubmitAvailable = (state: boolean) => {
        emailSubmitAvailable.value = state;
    };

    const handleVerificationSubmitAvailable = (state: boolean) => {
        verificationSubmitAvailable.value = state;
    };

    const handleVerificationCode = (code: string) => {
        verificationCode.value = code;
    };

    watch(() => authStore.user, (newValue) => {
        if (newValue) {
            emailValue.value = authStore.user.email || '';
        }
    }, { immediate: true });

    useModalActive();

    onBeforeUnmount(() => {
        if (intervalId.value) {
            clearTimeout(intervalId.value);
        }
    });
</script>

<style scoped lang="scss">
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.general-modal {
    @include modalMixins.modal;
}

.previous-button {
    position: absolute;
    top: 16px;
    left: 16px;
}
</style>
